import React from "react";
import { connect } from "react-redux";
import { formatMessage } from "lib/globalize-util";
import { ConfirmDialog, Loading } from "@nuorder/components";
import { CollectionType, ICreateAssortmentRequest } from "containers/folders/types/assortment";
import { AllocationDimensionOwnerObject } from "helpers/allocation-dimension";
import { listAllocationDimensionRequest } from "actions/allocation-dimension";
import { fetchSchema, getMerchantPreferenceRequest } from "actions/merchant";
import { FOLDERS } from "constants/integration/folders";
import { AssortmentTemplate, AssortmentType } from "containers/folders/types/base";
import { WindowAnalytics } from "helpers/window-analytics";
import { AssortmentActions } from "constants/analytics";
import { SchemaTypes } from "constants/merchant";
import { useFlags } from "launchdarkly-react-client-sdk";
import { FormValues, parseMerchantDimensionsIntoDropdownOptions } from "components";
import { usePrefetchBrandsForAssortment } from "containers/folders/hooks/brands";
import { IStore } from "reducers";
import {
    getMerchantAllocationDimensionState,
    getMerchantDefaultGenericAllocationDimensionsWithActiveValues,
} from "reducers/selectors/merchant";
import { createInitialFormValues, parseSelectedDimensionsIntoExcluded } from "./utils";
import { CreateAssortmentForm } from "./create-assortment";
import isEmpty from "lodash/isEmpty";
import me from "models/me";
import { IListState } from "reducers/merchant-allocation-dimension";
import { RequestStatus } from "constants/crud";

interface IActions {
    onFetchMerchantAllocationDimensions: (merchantId: string) => void;
    onFetchMerchantPreference: (merchantId: string) => void;
    onFetchMerchantSchema: (merchantId: string) => void;
}
type StateProps = { merchantDimensionsQuery: IListState };

export type CreateAssortmentProps = {
    onClose: () => void;
    onConfirm: (params: ICreateAssortmentRequest) => void;
    merchantId: string;
    parentId: string;
    isLoading?: boolean;
    errorMessage?: string;
    brandId: string;
    isVerticalAssortmentEnabled: boolean;
};

type CreateAssortmentDialogProps = StateProps & CreateAssortmentProps & IActions;

export const CreateAssortmentDialog: React.FunctionComponent<CreateAssortmentDialogProps> = ({
    onConfirm,
    isLoading,
    onClose,
    onFetchMerchantPreference,
    onFetchMerchantAllocationDimensions,
    onFetchMerchantSchema,
    merchantId,
    merchantDimensionsQuery,
    errorMessage,
    parentId,
    brandId,
    isVerticalAssortmentEnabled,
}) => {
    const { allocationBagVersion1 } = useFlags();

    const { queryStatus, result: merchantDimensions } = merchantDimensionsQuery;

    const queryRef = React.useRef(queryStatus);

    React.useEffect(() => {
        if (merchantId) {
            onFetchMerchantPreference(merchantId);
            onFetchMerchantAllocationDimensions(merchantId);
            onFetchMerchantSchema(merchantId);
            queryRef.current = RequestStatus.Active;
        }
    }, [merchantId, onFetchMerchantPreference, onFetchMerchantAllocationDimensions, onFetchMerchantSchema]);

    const isFetchingDimensions = !(queryRef.current === RequestStatus.Active && queryStatus === RequestStatus.Idle);

    usePrefetchBrandsForAssortment();

    const dimensionsDropdownOptions = React.useMemo(
        () => parseMerchantDimensionsIntoDropdownOptions(merchantDimensions),
        [merchantDimensions]
    );

    const formValues = React.useMemo(() => {
        if (isFetchingDimensions || (merchantDimensions.length === 0 && !isFetchingDimensions)) {
            return {} as FormValues; // this is safe because we only render the form when fetching is done
        }
        return createInitialFormValues(merchantDimensions);
    }, [isFetchingDimensions, merchantDimensions]);

    const handleCreateAssortment = React.useCallback(
        (formValues: FormValues) => {
            const excludedAllocationDimensionValues = parseSelectedDimensionsIntoExcluded(
                formValues.dimensionsValues,
                merchantDimensions
            );
            const collectionType = formValues.brandId ? CollectionType.External : CollectionType.Internal;
            onConfirm({
                merchantId: merchantId,
                brandId: formValues.brandId,
                excludedAllocationDimensionValues,
                parentId: parentId,
                name: formValues.assortmentName,
                inventoryEnabled: formValues.assortmentTemplate === AssortmentTemplate.Closeout,
                type: isVerticalAssortmentEnabled ? CollectionType.Vertical : collectionType,
                allocationBagVersion1: allocationBagVersion1,
                dimensionValues: formValues.dimensionsValues,
            });

            const doorDimension = merchantDimensions.find((dimension) => dimension.name === "Doors");
            const doorsSelection = doorDimension
                ? isEmptyDimension(doorDimension.id, excludedAllocationDimensionValues)
                    ? "ALL"
                    : "FEW"
                : "NONE";

            const deliveryDimension = merchantDimensions.find((dimension) => dimension.name === "Deliveries");
            const deliverySelection = deliveryDimension
                ? isEmptyDimension(deliveryDimension.id, excludedAllocationDimensionValues)
                    ? "ALL"
                    : "FEW"
                : "NONE";

            const userType = me.account_type;

            WindowAnalytics.track(AssortmentActions.CreateAssortment, {
                assortmentType: formValues.assortmentType == AssortmentType.Internal ? "Internal" : "External",
                doorsSelection: doorsSelection,
                deliverySelection: deliverySelection,
                userType: userType,
                template: formValues.assortmentTemplate,
                assortmentName: formValues.assortmentName,
            });
        },
        [merchantDimensions, onConfirm, merchantId, parentId, isVerticalAssortmentEnabled, allocationBagVersion1]
    );

    return isFetchingDimensions || isLoading ? (
        <LoadingDialog onClose={onClose} isSubmiting={isLoading} />
    ) : (
        <CreateAssortmentForm
            onSubmit={handleCreateAssortment}
            formValues={formValues}
            dimensionsDropdownOptions={dimensionsDropdownOptions}
            isLoading={isLoading}
            errorMessage={errorMessage}
            onClose={onClose}
            brandId={brandId}
            isVerticalAssortmentEnabled={isVerticalAssortmentEnabled}
        />
    );
};

const mapStateToProps = (state: IStore): StateProps => ({
    merchantDimensionsQuery: {
        ...getMerchantAllocationDimensionState(state).list,
        result: getMerchantDefaultGenericAllocationDimensionsWithActiveValues(state),
    },
});

const mapDispatchToProps = (dispatch, _ownProps) => {
    return {
        onFetchMerchantAllocationDimensions: (merchantId: string) =>
            dispatch(listAllocationDimensionRequest(merchantId, AllocationDimensionOwnerObject.Merchant)),
        onFetchMerchantPreference: (merchantId: string) => dispatch(getMerchantPreferenceRequest(merchantId)),
        onFetchMerchantSchema: (merchantId: string) => dispatch(fetchSchema(merchantId, SchemaTypes.Items)),
    };
};
export const ConnectedCreateAssortmentDialog = connect(mapStateToProps, mapDispatchToProps)(CreateAssortmentDialog);

type LoadingDialogProps = {
    onClose: () => void;
    isSubmiting: boolean;
};

const LoadingDialog = ({ onClose, isSubmiting }: LoadingDialogProps) => {
    const okLabel = formatMessage(isSubmiting ? "Creating Assortment" : "Create");

    const onCloseClickHandler = () => {
        //Ideally we would remove the close button but there is no way
        // so we just don't close
        if (!isSubmiting) {
            onClose();
        }
    };
    return (
        <ConfirmDialog
            contentProps={{ title: formatMessage("New Assortment") }}
            open={true}
            onConfirm={() => {}}
            onClose={onCloseClickHandler}
            hideCancelButton={isSubmiting}
            confirmButtonDisabled={true}
            data-test={FOLDERS.CREATE_ASSORTMENT_DIALOG.CONTAINER}
            okLabel={okLabel}
            cancelLabel={formatMessage("Cancel")}
            closeButtonAriaLabel={formatMessage("Close")}
        >
            <Loading />
        </ConfirmDialog>
    );
};

const isEmptyDimension = (dimenstionId: string, excludedAllocationDimensionValues: Record<string, string[]>) => {
    return (
        isEmpty(excludedAllocationDimensionValues) ||
        excludedAllocationDimensionValues[dimenstionId] === undefined ||
        excludedAllocationDimensionValues[dimenstionId]?.length === 0
    );
};
